/**
 * Role Helper Utilities
 * Multi-role support helpers for checking user roles
 */

/**
 * Check if user has a specific role
 * Supports both single-role (legacy) and multi-role users
 * 
 * @param {Object} user - User object from AuthContext
 * @param {string} roleName - Role name to check (e.g., 'Departments Manager')
 * @returns {boolean} - True if user has the role
 */
export const hasRole = (user, roleName) => {
  if (!user) return false;
  
  // Check multi-role array (preferred)
  if (user.roleNames && Array.isArray(user.roleNames)) {
    return user.roleNames.includes(roleName);
  }
  
  // Fallback to single role (legacy)
  return user.role === roleName;
};

/**
 * Check if user has any of the specified roles
 * 
 * @param {Object} user - User object from AuthContext
 * @param {string[]} roleNames - Array of role names to check
 * @returns {boolean} - True if user has at least one of the roles
 */
export const hasAnyRole = (user, roleNames) => {
  if (!user) return false;
  
  return roleNames.some(roleName => hasRole(user, roleName));
};

/**
 * Check if user has all of the specified roles
 * 
 * @param {Object} user - User object from AuthContext
 * @param {string[]} roleNames - Array of role names to check
 * @returns {boolean} - True if user has all of the roles
 */
export const hasAllRoles = (user, roleNames) => {
  if (!user) return false;
  
  return roleNames.every(roleName => hasRole(user, roleName));
};

/**
 * Get user's role names as array
 * 
 * @param {Object} user - User object from AuthContext
 * @returns {string[]} - Array of role names
 */
export const getRoleNames = (user) => {
  if (!user) return [];
  
  // Multi-role support
  if (user.roleNames && Array.isArray(user.roleNames)) {
    return user.roleNames;
  }
  
  // Single role legacy
  if (user.role) {
    return [user.role];
  }
  
  return [];
};

/**
 * Check if user is a Department Manager
 * (Common check across multiple components)
 * 
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const isDepartmentManager = (user) => {
  return hasRole(user, 'Department Manager');
};

/**
 * Check if user is Finance Admin
 * 
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const isFinanceAdmin = (user) => {
  return hasRole(user, 'Finance Admin');
};

/**
 * Check if user is Admin
 * 
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  return hasRole(user, 'Company Admin');
};

/**
 * Check if user is a Department Approver
 * (Approves invoices/expenses but does NOT manage the department)
 * 
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const isDepartmentApprover = (user) => {
  return hasRole(user, 'Approver');
};


